import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from '@/components/ui/use-toast';
import { ArrowLeft, ChevronRight, Loader2, Plus, School } from 'lucide-react';

export default function AdminSchoolCreator() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: '',
    city: '',
    state: '',
    zip: '',
    website: '',
    firstAdminEmail: '',
  });

  // Fetch existing schools
  const { data: schools, isLoading: loadingSchools } = useQuery({
    queryKey: ['admin-schools-list'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('schools')
        .select('id, name, city, state, created_at')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) return;

    setSubmitting(true);
    try {
      // If an email is provided, look up the user id
      let firstAdminUserId: string | undefined;
      if (form.firstAdminEmail.trim()) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('id')
          .eq('email', form.firstAdminEmail.trim().toLowerCase())
          .maybeSingle();
        if (!profile) {
          toast({ title: 'User not found', description: `No account found for ${form.firstAdminEmail}`, variant: 'destructive' });
          setSubmitting(false);
          return;
        }
        firstAdminUserId = profile.id;
      }

      const { data, error } = await supabase.functions.invoke('manage-school', {
        body: {
          action: 'create_school',
          name: form.name.trim(),
          city: form.city.trim() || undefined,
          state: form.state.trim() || undefined,
          zip: form.zip.trim() || undefined,
          website: form.website.trim() || undefined,
          firstAdminUserId,
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast({ title: 'School created', description: `"${form.name}" has been created successfully.` });
      setForm({ name: '', city: '', state: '', zip: '', website: '', firstAdminEmail: '' });
      queryClient.invalidateQueries({ queryKey: ['admin-schools-list'] });
    } catch (err: any) {
      toast({ title: 'Error creating school', description: err.message, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container mx-auto p-4 md:p-6 max-w-3xl space-y-6">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon" onClick={() => navigate('/passport')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-2xl font-bold">Admin: School Manager</h1>
      </div>

      {/* Create School Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" /> Create New School
          </CardTitle>
          <CardDescription>Create a school and optionally assign the first school admin by email.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">School Name *</Label>
              <Input id="name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required placeholder="e.g. AutoTech Academy" />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="city">City</Label>
                <Input id="city" value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} placeholder="City" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="state">State</Label>
                <Input id="state" value={form.state} onChange={e => setForm(f => ({ ...f, state: e.target.value }))} placeholder="e.g. TX" maxLength={2} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="zip">ZIP Code</Label>
                <Input id="zip" value={form.zip} onChange={e => setForm(f => ({ ...f, zip: e.target.value }))} placeholder="e.g. 75001" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="website">Website</Label>
                <Input id="website" value={form.website} onChange={e => setForm(f => ({ ...f, website: e.target.value }))} placeholder="https://..." />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="adminEmail">First Admin Email (optional)</Label>
              <Input id="adminEmail" type="email" value={form.firstAdminEmail} onChange={e => setForm(f => ({ ...f, firstAdminEmail: e.target.value }))} placeholder="admin@school.edu" />
              <p className="text-xs text-muted-foreground">If blank, you will be assigned as the school admin.</p>
            </div>

            <Button type="submit" disabled={submitting || !form.name.trim()}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              Create School
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Existing Schools */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <School className="h-5 w-5" /> Existing Schools
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loadingSchools ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : !schools?.length ? (
            <p className="text-muted-foreground text-sm text-center py-4">No schools created yet.</p>
          ) : (
            <div className="space-y-2">
              {schools.map(s => (
                <div
                  key={s.id}
                  className="flex items-center justify-between p-3 rounded-md border bg-muted/30 cursor-pointer hover:bg-accent/50 transition-colors group"
                  onClick={() => navigate('/school')}
                >
                  <div>
                    <p className="font-medium">{s.name}</p>
                    <p className="text-xs text-muted-foreground">{[s.city, s.state].filter(Boolean).join(', ') || 'No location'}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <p className="text-xs text-muted-foreground">{new Date(s.created_at).toLocaleDateString()}</p>
                    <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
